import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import './DealActivityEvent.sass';

import MarkdownLabel from '@payaca/components/markdownLabel/MarkdownLabel';
import TruncatedHtml from '@payaca/components/TruncatedHTML/TruncatedHTML';
import { DealActivityEventType } from '@payaca/types/dealActivityTypes';
import { TIconName } from '@payaca/untitled-icons';
import { isValidHtml } from '@payaca/utilities/htmlUtilities';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import {
  IntegratedEmailProjectActivityEvent,
  ProjectActivityEvent,
} from '@/gql/graphql';
import DealActivityCard from '@/ui/components/dealActivityCard/DealActivityCard';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import { useHistory } from 'react-router-dom';
import DealActivityEventPrimaryAction from './DealActivityEventPrimaryAction';

type Props = {
  projectActivityEvent:
    | ProjectActivityEvent
    | IntegratedEmailProjectActivityEvent;
  projectId: string;
};

const DealActivityEvent: FC<Props> = (props) => {
  const { projectActivityEvent, projectId } = props;
  const history = useHistory();
  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  const iconName = useMemo<TIconName>(() => {
    switch (projectActivityEvent.type as DealActivityEventType) {
      case 'changeProposalSent':
      case 'proposalSent':
      case 'estimateSent':
      case 'estimateResent':
      case 'invoiceSent':
      case 'invoicePaymentReminderSent':
      case 'purchaseOrderSent':
      case 'emailSendAttempted':
      case 'integratedEmailSendAttempted':
      case 'integratedEmailReceived':
      case 'inboundEmailAssigned':
      case 'smsSendAttempted':
        return 'mail-02.3';
      case 'proposalViewed':
      case 'changeProposalViewed':
      case 'estimateViewed':
      case 'invoiceViewed':
        return 'eye.3';
      case 'proposalDeclined':
      case 'changeProposalDeclined':
        return 'x-close';
      case 'changeProposalAccepted':
      case 'proposalAccepted':
        return 'check';
      case 'paymentCompleted':
        return 'currency-pound';
      case 'automationActionRan':
        return 'plus-circle.3';
      case 'note':
        return 'edit-01.3';
      case 'changeProposalVoided':
      case 'invoiceVoided':
      case 'changeProposalExpired':
      case 'proposalExpired':
      case 'invoiceOverdue':
        return 'file-x-01.3';
      case 'estimateBounced':
        return 'alert-triangle.3';
      case 'taskMarkedComplete':
        return 'check-circle.3';
      default:
        return 'info-circle.3';
    }
  }, [projectActivityEvent]);

  const willHaveLongDescription = useMemo(() => {
    switch (projectActivityEvent.type as DealActivityEventType) {
      case 'note':
      case 'emailSendAttempted':
      case 'smsSendAttempted':
      case 'integratedEmailSendAttempted':
      case 'integratedEmailReceived':
      case 'inboundEmailAssigned':
        return true;
      default:
        return false;
    }
  }, [projectActivityEvent]);

  const isHtml = useMemo(() => {
    return isValidHtml(projectActivityEvent.description);
  }, [projectActivityEvent.description]);

  return (
    <DealActivityCard
      onClick={
        projectActivityEvent.type === 'note'
          ? () =>
              history.push(
                `/projects/${projectId}/activity/${projectActivityEvent.linkedEntity?.id}`
              )
          : undefined
      }
      iconName={iconName}
      whiteSpacePreWrap={isHtml}
      title={
        willHaveLongDescription ? (
          <p className="font-semibold">{projectActivityEvent.name}</p>
        ) : (
          <MarkdownLabel markdown={projectActivityEvent.description} />
        )
      }
      titleSuffix={
        <>
          <DealActivityEventPrimaryAction
            projectActivityEvent={projectActivityEvent}
          />

          <span className="whitespace-nowrap">
            {format(
              projectActivityEvent.timestamp,
              getInternationalDateFormatByLocale(
                DateFormats.TIMESTAMP_SHORT,
                accountRegionPreferences?.locale
              )
            )}
          </span>
        </>
      }
    >
      {willHaveLongDescription &&
        (isHtml ? (
          <TruncatedHtml
            className="deal-activity-event-html-description"
            html={projectActivityEvent.description}
          />
        ) : (
          <MarkdownLabel
            truncatedClassName="container-bottom-shadow relative"
            limitLines={8}
            markdown={projectActivityEvent.description}
            truncate
          />
        ))}
    </DealActivityCard>
  );
};

export default DealActivityEvent;
